import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import {
  Box,
  Card,
  Flex,
  Grid,
  Divider,
  LoadingOverlay,
  Text,
  Tabs,
  Space,
  Button,
  Group,
  Overlay,
  Loader,
  UnstyledButton,
} from "@mantine/core";
import { shallow } from "zustand/shallow";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import {
  IconSparkles,
  IconTool,
  IconDeviceFloppy,
  IconCamera,
  IconPin,
  IconCaretUp,
  IconCaretDown,
  IconFlask,
  IconTrash,
  IconListDetails,
} from "@tabler/icons-react";
import toast from "react-hot-toast";

import { ReportRecipePinnedList } from "@components/ReportRecipe";
import ReportRecipeSetupList from "./ReportRecipeSetupList";
import ReportRecipeEntityPicker from "./ReportRecipeEntityPicker";
import ReportRecipeFallback from "./ReportRecipeFallback";
import ReportRecipeOptions from "./ReportRecipeOptions";
import ReportRecipeQueryComponents from "./ReportRecipeQueryComponents";
import ReportRecipeVisualizer from "./ReportRecipeVisualizer";
import ReportSnapshotCreate from "./ReportSnapshotCreate";
import ReportSnapshotList from "./ReportSnapshotList";
import SnapshotView from "./SnapshotView";
import SavedRecipeMeta from "./SavedRecipeMeta";
import SavedReportRecipeCreate from "./SavedReportRecipeCreate";
import SavedReportRecipeList from "./SavedReportRecipeList";
import ReportRecipePinnableOptions from "./ReportRecipePinnableOptions";
import ResponseFilter from "./ResponseFilter";
import qbValidator from "./qb-validator";
import qbGenerateRequest from "./qb-generate-request";
import qbInit from "./qb-init";
import qbHasChanged from "./qb-has-changed";
import entityIcon from "@util/entityIcon";
import { UserGroupCreate } from "@components/UserGroup";
import qbSnapshotQueryVisualizerData from "./qb-snapshot-query-visualizer-data";

import useAppStore from "./report-recipe-runner-store";

const formatter = new Intl.ListFormat("en", {
  style: "long",
  type: "conjunction",
});

export default function ReportRecipeRunner({ info, id, setupData, showSetup }) {
  const reportDataRef = useRef(null);
  const adminInfo = useSelector((state) => state.admin);
  const managerInfo = useSelector((state) => state.manager);
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [collapsed, setCollapsed] = useState(false);
  const [resetting, setResetting] = useState(false);
  const queryComponentsValid = qbValidator();
  const userParams = {
    savedRecipeId: searchParams.get("savedReportRecipeId") || "",
  };

  const fetchRecipeDetail = () =>
    queryClient.invalidateQueries([`reportRecipeDetail`, id, userParams]);

  const fetchSnapshots = () => queryClient.invalidateQueries([`snapshots`]);

  const fetchReportRecipeSetupPolling = () =>
    queryClient.invalidateQueries([`reportRecipeSetupPolling`, id]);

  const isAdmin = adminInfo ? true : false;
  const isOrgManager = managerInfo?.organization_id ? true : false;
  const isLocationManager = managerInfo?.location_id ? true : false;

  const {
    activeTab,
    error,
    initialQueryData,
    locations,
    orgs,
    loading,
    managerQueryEntity,
    queryComponents,
    queryData,
    recipeInfo,
    reportData,
    reportDataQuery,
    reset,
    snapshot,
    setError,
    setLoading,
    setLocations,
    setOrgs,
    setReportData,
    setQueryData,
    savedRecipe,
    setActiveTab,
    setInitialQueryData,
    setManagerQueryEntity,
    setQueryComponents,
    setRecipeInfo,
    setReportDataQuery,
    setSavedRecipe,
    setSnapshot,
    statBlocks,
    tableConfig,
    setStatBlocks,
    setTableConfig,
  } = useAppStore(
    (state) => ({
      activeTab: state.active_tab,
      error: state.error,
      loading: state.loading,
      locations: state.locations,
      orgs: state.orgs,
      managerQueryEntity: state.manager_query_entity,
      queryComponents: state.query_components,
      queryData: state.query_data,
      initialQueryData: state.initial_query_data,
      recipeInfo: state.recipe_info,
      reportData: state.report_data,
      reportDataQuery: state.report_data_query,
      reset: state.reset,
      savedRecipe: state.saved_recipe,
      snapshot: state.snapshot,
      setActiveTab: state.setActiveTab,
      setError: state.setError,
      setInitialQueryData: state.setInitialQueryData,
      setOrgs: state.setOrgs,
      setLoading: state.setLoading,
      setLocations: state.setLocations,
      setManagerQueryEntity: state.setManagerQueryEntity,
      setQueryData: state.setQueryData,
      setQueryComponents: state.setQueryComponents,
      setRecipeInfo: state.setRecipeInfo,
      setReportData: state.setReportData,
      setReportDataQuery: state.setReportDataQuery,
      setSavedRecipe: state.setSavedRecipe,
      setSnapshot: state.setSnapshot,
      statBlocks: state.stat_blocks,
      tableConfig: state.table_config,
      setStatBlocks: state.setStatBlocks,
      setTableConfig: state.setTableConfig,
    }),
    shallow
  );

  const isOrgManagerWithoutLocations =
    isOrgManager && managerInfo.locations.length === 0;

  useEffect(() => {
    return () => {
      reset(true);
    };
  }, []);

  useEffect(() => {
    if (error) {
      toast.error(error);
      setTimeout(() => {
        setError(null);
      }, 1000);
    }
  }, [error]);

  useEffect(() => {
    setupRunner();
  }, []);

  useEffect(() => {
    if (savedRecipe) return;
    if (showSetup) {
      setActiveTab("setup");
    } else {
      setActiveTab("form");
    }
  }, [showSetup]);

  function setupRunner() {
    setRecipeInfo({
      ...info,
    });

    const formattedQueryComponents = info.query_components
      .map((m) => ({
        ...m,
        clientName:
          m.type === "effortPicker" ? "campaign_effort_ids" : m.server_name,
      }))
      .sort((a, b) => a.index - b.index);

    setQueryComponents(formattedQueryComponents);
    const queryDataChanges = {
      ...qbInit(formattedQueryComponents),
    };

    if (info.visualizer_options.length === 1) {
      queryDataChanges.visualizer = info.visualizer_options[0].value;
    }

    const qd = savedRecipe
      ? {
          ...queryData,
          ...queryDataChanges,
          ...savedRecipe.query_data,
        }
      : {
          ...queryData,
          ...queryDataChanges,
        };

    if (info?.custom_report_comp === "response_filter") {
      if (savedRecipe) {
        qd.campaign_effort_id =
          savedRecipe.query_data?.campaign_effort_id || "";
        qd.campaign_efforts_preserved_options =
          savedRecipe.query_data.campaign_efforts_preserved_options || [];
        qd.selected_questions = savedRecipe.query_data.selected_questions || [];
      } else {
        qd.campaign_effort_id = "";
        qd.campaign_efforts_preserved_options = [];
        qd.selected_questions = [];

        if (isLocationManager) {
          setLocations([
            { id: managerInfo.location_id, name: managerInfo.location_name },
          ]);
        }
      }
    }

    setQueryData(qd);
    setInitialQueryData(qd);

    if (searchParams.get("savedReportRecipeId")) {
      setSavedRecipe({
        id: searchParams.get("savedReportRecipeId"),
        title: "",
      });
    }

    if (isOrgManagerWithoutLocations) {
      setManagerQueryEntity("organization", true);
    }
  }

  useEffect(() => {
    setError(null);
  }, [JSON.stringify(queryData)]);

  useEffect(() => {
    if (!savedRecipe || savedRecipe.detailed) return;
    fetchSavedRecipe();
  }, [JSON.stringify(savedRecipe)]);

  // useEffect(() => {
  //   if (!reportData) return;
  //   runReport();
  // }, [queryData.visualizer]);

  function runReport() {
    if (
      adminInfo &&
      !locations.length &&
      !orgs.length &&
      recipeInfo.show_org_loc_select
    )
      return reset();

    const queryReq = qbGenerateRequest(queryComponents, queryData, recipeInfo);
    setReportDataQuery(queryData);

    const req = {
      ...queryReq,
    };

    if (locations.length) {
      req.location_ids = locations.map((m) => m.id);
      req.saved_recipe_locations = locations;
    }

    if (orgs.length) {
      req.organization_ids = orgs.map((m) => m.id);
      req.saved_recipe_orgs = orgs;
    }

    if (managerQueryEntity === "organization") {
      req.use_organization_for_query = true;
    }

    setLoading(true);

    axios
      .post(`/report-recipes/${id}/run-report/`, req)
      .then(({ data }) => {
        afterRunReport();
        // if (data.response[0].response_message) {
        //   setReportData(null);
        //   return setError(data.response[0].response_message);
        // }
        // setReportData(data.response[0]);
        // if (reportDataRef.current) {
        //   reportDataRef.current.scrollIntoView({
        //     behavior: "smooth",
        //   });
        // }
      })
      .then(() => {
        setLoading(false);
      })
      .catch((err) => {
        setReportData(null);
        setLoading(false);
      });
  }

  function afterRunReport() {
    fetchReportRecipeSetupPolling();
    setActiveTab("setup");
    setReportData(null);
    setStatBlocks([]);
    setTableConfig(null);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }

  function fetchSavedRecipe() {
    if (!savedRecipe) return;
    axios
      .get(`/saved-report-recipes/${savedRecipe.id}/`)
      .then(({ data }) => {
        const res = data.response[0];
        setSavedRecipe({
          ...res,
          detailed: true,
        });
        if (res.query_data) {
          if (res.query_data.saved_recipe_locations) {
            setLocations(res.query_data.saved_recipe_locations);
          }
          if (res.query_data.saved_recipe_orgs) {
            setOrgs(res.query_data.saved_recipe_orgs);
          }
        }
        return res.query_data;
      })
      .then((qd) => {
        if (qd) {
          setQueryData(qd);
        }
      })
      .catch((err) => {
        setError(err);
      });
  }

  async function onSavedRecipeSelect(savedReportItem) {
    setSavedRecipe(savedReportItem);
    // clear snapshot
    setSnapshot(null);
    setActiveTab("form");
    if (savedReportItem.query_data) {
      setQueryData({
        ...queryData,
        ...savedReportItem.query_data,
      });
    }
  }

  function getSnapshotQueryVisualizerData() {
    return qbSnapshotQueryVisualizerData(
      queryComponents,
      {
        ...queryData,
      },
      recipeInfo,
      {
        locations,
        organizations: orgs,
      }
    );
  }

  if (!recipeInfo) return null;

  const queryChanged = qbHasChanged(queryData, reportDataQuery);

  const hasInProgressSetup = setupData.find((m) => m.completed === false)
    ? true
    : false;

  const reportRunnable =
    queryComponentsValid && !hasInProgressSetup ? true : false;
  // queryComponentsValid && queryData.visualizer ? true : false;

  const hasQueryComponents = JSON.stringify(queryComponents) !== "[]";
  // const showEntityPicker = recipeInfo.show_org_loc_select && !isLocationManager;
  const showEntityPicker =
    (recipeInfo.show_loc_select ||
      recipeInfo.show_org_select ||
      recipeInfo.show_org_loc_select) &&
    !isLocationManager;
  const entityRequirementsSatisfied =
    managerQueryEntity === "organization" ||
    !showEntityPicker ||
    orgs.length > 0 ||
    locations.length > 0;

  const showSavedRecipes =
    (adminInfo && !recipeInfo.snapshot_only_admin) ||
    (managerInfo &&
      managerInfo.location_id &&
      !recipeInfo.snapshot_only_location) ||
    (managerInfo &&
      managerInfo.organization_id &&
      !recipeInfo.snapshot_only_organization);

  const defaultSnapshotTitle = `${
    savedRecipe?.title || recipeInfo.title
  } ${new Date()
    .toLocaleString("en-US", { timeZoneName: "short" })
    .replace(",", "")
    .replace("at", "")}`;

  const entitySectionVisible =
    !savedRecipe && showEntityPicker && !isOrgManagerWithoutLocations;

  return (
    <>
      <Grid>
        <Grid.Col span={{ base: 12, md: 12 }}>
          <Card
            style={{
              position: "relative",
              overflow: "visible",
            }}
          >
            <LoadingOverlay visible={loading} zIndex={99} />
            {savedRecipe && (
              <>
                <SavedRecipeMeta />
                <Divider mt="lg" mb="lg" />
              </>
            )}
            <Tabs
              value={activeTab}
              keepMounted={false}
              onChange={(e) => {
                setActiveTab(e);
                setCollapsed(false);
              }}
            >
              <Tabs.List mb="md">
                <Tabs.Tab
                  leftSection={<IconTool size={24} />}
                  value="form"
                  disabled={snapshot ? true : false}
                >
                  Builder
                </Tabs.Tab>
                <Tabs.Tab
                  leftSection={
                    setupData.find((m) => m.complete === false) ? (
                      <Loader size="sm" type="dots" color="dark" />
                    ) : (
                      <IconListDetails size={24} />
                    )
                  }
                  value="setup"
                  disabled={snapshot ? true : false}
                >
                  Queue
                </Tabs.Tab>
                {showSavedRecipes && (
                  <Tabs.Tab
                    leftSection={<IconDeviceFloppy size={24} />}
                    value="saved"
                  >
                    Saved
                  </Tabs.Tab>
                )}
                <Tabs.Tab
                  leftSection={entityIcon.snapshot(24)}
                  value="snapshots"
                  // disabled={hasInProgressSetup}
                >
                  Snapshots
                </Tabs.Tab>
                <Tabs.Tab leftSection={<IconPin size={24} />} value="pins">
                  Pins
                </Tabs.Tab>
              </Tabs.List>
              <CollapsiblePanel collapsed={collapsed}>
                <Tabs.Panel value="form">
                  {recipeInfo?.custom_report_comp === "response_filter" ? (
                    <>
                      <ResponseFilter
                        showEntityPicker={entitySectionVisible}
                        afterRunReport={afterRunReport}
                        showSavedRecipeCreate={!savedRecipe && showSavedRecipes}
                      />
                    </>
                  ) : (
                    <>
                      {entitySectionVisible && (
                        <>
                          <ReportRecipeEntityPicker />
                          {entityRequirementsSatisfied && (
                            <Divider mt="lg" mb="lg" />
                          )}
                        </>
                      )}
                      {entityRequirementsSatisfied && (
                        <>
                          {hasQueryComponents && !resetting && (
                            <>
                              <ReportRecipeQueryComponents />
                              <Divider mt="lg" mb="lg" />
                            </>
                          )}
                          {/* <>
                        <ReportRecipeOptions />
                        <Divider mt="lg" mb="lg" />
                      </> */}
                        </>
                      )}
                      {entityRequirementsSatisfied && (
                        <>
                          {hasInProgressSetup && (
                            <Text fw={600} size="sm" c="red" mt="md" mb="md">
                              Please allow in progress items to finish before
                              running a report
                            </Text>
                          )}
                          <Button
                            color="green"
                            size="md"
                            leftSection={<IconSparkles />}
                            onClick={runReport}
                            disabled={!reportRunnable}
                            fullWidth
                          >
                            Run Report
                          </Button>
                          {!savedRecipe && showSavedRecipes && (
                            <Box mt="lg">
                              <SavedReportRecipeCreate
                                recipeId={id}
                                reqData={{
                                  report_recipe_id: id,
                                  // orgs,
                                  // locations,
                                  query_data: {
                                    ...queryData,
                                    saved_recipe_orgs: orgs,
                                    saved_recipe_locations: locations,
                                    organization_ids: orgs.map((m) => m.id),
                                    location_ids: locations.map((m) => m.id),
                                  },
                                }}
                              />
                              <UnstyledButton
                                // style={{
                                //   color: "var(--mantine-color-gray-5)",
                                // }}
                                mt="md"
                                onClick={() => {
                                  reset();
                                  setResetting(true);
                                  setTimeout(() => {
                                    setResetting(false);
                                  }, 1);
                                }}
                              >
                                <Flex gap="xs" align="center">
                                  <IconTrash />
                                  <Text fw={600} size="sm">
                                    Clear form selections
                                  </Text>
                                </Flex>
                              </UnstyledButton>
                            </Box>
                          )}
                        </>
                      )}
                    </>
                  )}
                </Tabs.Panel>
                <Tabs.Panel value="saved">
                  {showSavedRecipes && (
                    <>
                      <Box>
                        <SavedReportRecipeList
                          reqData={{
                            report_recipe_id: id,
                          }}
                          recipeId={id}
                          selectedId={savedRecipe ? savedRecipe.id : null}
                          onSelect={(savedReportItem) => {
                            onSavedRecipeSelect(savedReportItem).then(() => {
                              fetchSnapshots();
                            });
                          }}
                        />
                      </Box>
                    </>
                  )}
                </Tabs.Panel>
                <Tabs.Panel value="snapshots">
                  <ReportSnapshotList
                    onSelect={(selectedSnapshot) => {
                      if (!selectedSnapshot) return;
                      if (selectedSnapshot?.visualizer) {
                        setQueryData({
                          ...queryData,
                          visualizer: selectedSnapshot.visualizer,
                        });
                      }
                      setSnapshot(selectedSnapshot);
                    }}
                    selectedId={snapshot ? snapshot.id : null}
                  />
                </Tabs.Panel>
                <Tabs.Panel value="pins">
                  {!snapshot && (
                    <div>
                      <ReportRecipePinnableOptions
                        divider={false}
                        fetchSavedRecipe={() => fetchSavedRecipe()}
                      />
                      <Divider mt="lg" mb="lg" />
                    </div>
                  )}
                  <ReportRecipePinnedList
                    fetchData={() => {
                      if (savedRecipe) {
                        fetchSavedRecipe();
                      } else {
                        fetchRecipeDetail();
                      }
                    }}
                    items={
                      savedRecipe ? savedRecipe.pinned_to : recipeInfo.pinned_to
                    }
                  />
                </Tabs.Panel>
                <Tabs.Panel value="setup">
                  <ReportRecipeSetupList data={setupData} />
                </Tabs.Panel>
              </CollapsiblePanel>
            </Tabs>
            <Group justify="center" mt={collapsed ? 0 : "lg"}>
              <Button
                color="gray"
                variant="subtle"
                size="xs"
                leftSection={
                  collapsed ? (
                    <IconCaretDown
                      color="var(--mantine-color-gray-5)"
                      fill="var(--mantine-color-gray-5)"
                      size={16}
                    />
                  ) : (
                    <IconCaretUp
                      color="var(--mantine-color-gray-5)"
                      fill="var(--mantine-color-gray-5)"
                      size={16}
                    />
                  )
                }
                onClick={() => setCollapsed(!collapsed)}
              >
                {collapsed ? "Expand section" : "Collapse section"}
              </Button>
            </Group>
          </Card>
        </Grid.Col>
        {(reportData || snapshot) && (
          <Grid.Col
            span={{ base: 12, md: 12 }}
            ref={reportDataRef}
            data-ref="reportData"
          >
            <Card style={{ minHeight: "450px" }}>
              {snapshot && <SnapshotView fetchSnapshots={fetchSnapshots} />}
              {!reportData && !snapshot && (
                <div
                  style={{
                    display: "flex",
                    height: "100%",
                    width: "100%",
                    justifyContent: "center",
                    flexDirection: "column",
                    alignItems: "center",
                    align: "center",
                    textAlign: "center",
                    flexGrow: 1,
                  }}
                >
                  <IconFlask size={80} />
                  <Text size="xl" fw={600}>
                    What insights will you gain next?
                  </Text>
                </div>
              )}
              {reportData && !snapshot && (
                <Box>
                  {queryChanged && (
                    <Overlay
                      color="#fff"
                      blur={0}
                      backgroundOpacity={0.85}
                      zIndex={99}
                    >
                      <Box
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "100%",
                          flexDirection: "column",
                          width: "450px",
                          margin: "0 auto",
                          textAlign: "center",
                        }}
                      >
                        <Box
                          style={{
                            border: "2px solid var(--mantine-color-dark-9)",
                            background: "#fff",
                          }}
                          p="xl"
                        >
                          <Text fw={600} size="xl">
                            You have edited your report criteria
                          </Text>
                          <Button
                            size="lg"
                            mb="lg"
                            mt="lg"
                            fullWidth
                            onClick={runReport}
                            disabled={!reportRunnable}
                          >
                            Get results
                          </Button>
                          <Text fw={600}>or keep editing your report</Text>
                          <Button
                            size="xs"
                            variant="subtle"
                            color="gray"
                            mt="sm"
                            onClick={() => setQueryData(reportDataQuery)}
                          >
                            clear changes
                          </Button>
                        </Box>
                      </Box>
                    </Overlay>
                  )}
                  {reportData.fallback ? (
                    <div>
                      <ReportRecipeFallback />
                    </div>
                  ) : (
                    <ReportRecipeVisualizer queryChanged={queryChanged} />
                  )}
                  <Divider mt="lg" mb="lg" />
                  <Flex gap="xl">
                    <Flex style={{ flexGrow: 1 }} align="center" gap="xl">
                      {!snapshot && reportData && (
                        <div>
                          <ReportSnapshotCreate
                            recipeId={id}
                            reqData={{
                              chart_data: reportData,
                              report_recipe_id: !savedRecipe ? id : null,
                              visualizer: queryData.visualizer,
                              saved_report_recipe_id: savedRecipe
                                ? savedRecipe.id
                                : null,
                              table_config: tableConfig,
                              meta_block_data: statBlocks,
                            }}
                          />
                        </div>
                      )}
                      <UserGroupCreate
                        reqData={{
                          create_snapshot: true,
                          data_viz: getSnapshotQueryVisualizerData(),
                          chart_data: reportData,
                          report_recipe_id: !savedRecipe ? id : null,
                          visualizer: queryData.visualizer,
                          saved_report_recipe_id: savedRecipe
                            ? savedRecipe.id
                            : null,
                          table_config: tableConfig,
                          meta_block_data: statBlocks,
                        }}
                        buttonProps={{
                          size: "sm",
                          variant: "light",
                        }}
                        formProps={{
                          name: "",
                          // name:
                          //   snapshot?.title ||
                          //   savedRecipe?.title ||
                          //   recipeInfo.title,
                          description: `generated from ${
                            snapshot?.title ||
                            savedRecipe?.title ||
                            recipeInfo.title
                          } (${
                            snapshot
                              ? "snapshot"
                              : savedRecipe
                              ? "saved recipe"
                              : "recipe"
                          }) ${new Date()
                            .toLocaleString("en-US", { timeZoneName: "short" })
                            .replace(",", "")
                            .replace("at", "")}`,
                          // description: `generated from ${
                          //   snapshot?.title ||
                          //   savedRecipe?.title ||
                          //   recipeInfo.title
                          // } ${new Date()
                          //   .toLocaleString("en-US", { timeZoneName: "short" })
                          //   .replace(",", "")
                          //   .replace("at", "")}`,
                        }}
                      />
                    </Flex>
                    <Button
                      variant="subtle"
                      color="gray"
                      onClick={() => {
                        setReportData(null);
                      }}
                    >
                      Clear Data
                    </Button>
                  </Flex>
                </Box>
              )}
            </Card>
          </Grid.Col>
        )}
      </Grid>
    </>
  );
}

const CollapsiblePanel = styled.div`
  display: ${(props) => (props.collapsed ? "none" : "block")};
`;
